import Button from "app/components/button/button";
import CircleLoader from "app/components/loader/CircleLoader";
import Notification from "app/components/notification/notification";
import { SolanaConnectButton } from "app/components/solana-connect-button/SolanaConnectButton";
import { TonWalletGate } from "app/components/ton-connect-button/TonWalletGate";
import { useAuth } from "app/hooks";
import { useDeviceDetection } from "app/hooks/useDeviceDetection";
import { useSolanaWalletAssociation } from "app/hooks/useSolanaWalletAssociation";
import { useSafeSignMessage } from "app/hooks/useSafeSignMessage";
import { useTonWalletAssociation } from "app/hooks/useTonWalletAssociation";
import { useTheme } from "app/shared";
import { ONE_MINUTE } from "app/types/constants";
import { checkBalance } from "app/utils/checkBalance";
import { zeroAddress } from "app/utils/constants";
import { trackSaleSuccess } from "app/utils/ido-funnel-tracking";
import { useNetworkManager } from "app/hooks/useNetworkManager";
import {
  usePurchaseLogger,
  useErrorLogger,
  useUserActionLogger,
} from "app/hooks/useLogger";
import RunningLine from "app/widgets/RunningLine/RunningLine";
import RefModal from "app/widgets/referral-modal/RefModal";
import usdc from "assets/img/icons/usdc.svg";
import usdt from "assets/img/icons/usdt.svg";
import axios from "axios";
import { FC, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import tokenService from "services/token.service";
import { useGetLatestPurchasesQuery } from "store/api/new.api";
import { parseUnits } from "viem";
import {
  useAccount,
  useBalance,
  useReadContract,
  useSwitchChain,
  useWaitForTransactionReceipt,
  useWriteContract,
} from "wagmi";
import ABI from "../../../config/contract/abi.json";
import tokenABI from "../../../config/contract/tokenABI.json";
import InviteSection from "../PreOrderForm/InviteSection";
import { Tab } from "../PreOrderForm/PreOrderForm";
import NetworkSelector from "../network-guard/network-selector";
import { MAINNET_CONTRACTS, TESTNET_CONTRACTS } from "./networks.types";
import {
  SALE_FLOW_STAGE,
  SALE_FLOW_TIMEOUT_ERROR,
  SALE_FLOW_TIMEOUT_MS,
  SALE_PENDING_ERROR,
  SALE_SUPPORTED_CHAIN_ERROR,
} from "./sale-flow.constants";
import {
  buildSaleReceiptError,
  buildSaleRevertedError,
  getEffectiveMinAllocationUsd,
  getStablecoinDecimals,
  getSupportedChainIds,
  isSupportedChainId,
  normalizeChainId,
} from "./sale-flow.utils";
import styles from "./sale-widget.module.scss";

const tabs = [
  { label: "USDC", icon: usdc },
  { label: "USDT", icon: usdt },
];

export enum Currency {
  USDT = "USDT",
  USDC = "USDC",
  ETH = "ETH",
}

export interface AllocationDetails {
  maxAllocation: number;
  usedAllocation: number;
  availableAllocation: number;
  tokenPrice: number;
  minAllocation: number;
}

export interface PurchaseDetails {
  totalTokensPurchased: number;
  claimableTokens: number;
  vestingDetails: string;
}

interface SaleDetails {
  isSaleOpen: boolean;
}

interface SaleWidgetProps {
  allocationDetails: AllocationDetails;
  purchaseDetails: PurchaseDetails;
  saleDetails: SaleDetails;
  tokenTicker: string;
  isLoading: boolean;
  onPurchaseSuccess: () => void;
  sellStartDate: Date;
  sellEndDate: Date;
  totalBalance: number;
  totalTokens: number;
  idoData: any;
  message: string;
  ticker?: string;
}

export const isDevelopment =
  process.env.REACT_APP_ENVIRONMENT === "development";

export const getContractAddressesByEnv = (): any => {
  return isDevelopment ? TESTNET_CONTRACTS : MAINNET_CONTRACTS;
};

const SaleWidget: FC<SaleWidgetProps> = ({
  allocationDetails,
  purchaseDetails,
  saleDetails,
  tokenTicker,
  isLoading,
  totalTokens,
  totalBalance,
  onPurchaseSuccess,
  sellStartDate,
  sellEndDate,
  idoData,
  ticker,
  message,
}) => {
  const { theme } = useTheme();
  const isAuth = useAuth();
  const { isMobile, isMobileWithoutExtension } = useDeviceDetection();
  const [isPurchaseSuccessful, setIsPurchaseSuccessful] =
    useState<boolean>(false);
  const [isCancelSuccessful, setIsCancelSuccessful] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<Tab>(tabs[0]);

  // Logging hooks
  const {
    logPurchaseAttempt,
    logPurchaseSuccess,
    logPurchaseError,
    logPurchaseCancel,
  } = usePurchaseLogger();
  const { logJavaScriptError } = useErrorLogger();
  const { logButtonClick } = useUserActionLogger();

  const [isSignatureSaved, setIsSignatureSaved] = useState<boolean>(false);
  const { idoId } = useParams<{ idoId: string }>();
  const [amountInUSD, setAmountInUSD] = useState<string>("250");
  const [tokensToReceive, setTokensToReceive] = useState<string>("");
  const [currentPurchase, setCurrentPurchase] =
    useState<PurchaseDetails | null>(purchaseDetails || null);
  const [currentAllocation, setCurrentAllocation] =
    useState<AllocationDetails>(allocationDetails);

  // Solana wallet integration
  const requiresSolana = idoData?.solanaSupport === true;
  const { isAssociated: isSolanaAssociated, error: solanaAssociationError } =
    useSolanaWalletAssociation({ requiresSolana });
  const [solanaError, setSolanaError] = useState<string | null>(null);

  // TON wallet integration
  const requiresTon = idoData?.tonSupport === true;
  const tonAssociationState = useTonWalletAssociation({ requiresTon });
  const { isAssociated: isTonAssociated, error: tonAssociationError } =
    tonAssociationState;

  useEffect(() => {
    setCurrentAllocation(allocationDetails);
    setCurrentPurchase(purchaseDetails);
  }, [allocationDetails, purchaseDetails]);

  const [isLoadingAA, setIsLoading] = useState<boolean>(false);
  const [approveHash, setApproveHash] = useState<any | string | undefined>(
    undefined
  );
  const [buyingHash, setBuyingHash] = useState<any | string | undefined>(
    undefined
  );
  const [buyTxChainId, setBuyTxChainId] = useState<number | null>(null);

  useEffect(() => {
    const tokenPrice = parseFloat(currentAllocation.tokenPrice.toString());

    const amount = parseFloat(amountInUSD);

    setTokensToReceive((amount / tokenPrice).toString());
  }, [amountInUSD, currentAllocation]);

  const [mySignature, setMySignature] = useState<string | null>(null);

  const [showTxNotification, setShowTxNotification] = useState(false);
  const [showBuyNotification, setShowBuyNotification] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [referralLink, setReferralLink] = useState<string>("");
  const [walletActionPending, setWalletActionPending] = useState<string | null>(
    null
  );
  const purchaseFlowRef = useRef<string | null>(null);
  const purchaseSignatureRef = useRef<string | null>(null);
  const [purchaseFlowStage, setPurchaseFlowStage] = useState<string | null>(
    null
  );
  const [showWalletActionNotification, setShowWalletActionNotification] =
    useState(false);

  const handleReferralLinkUpdate = (newLink: string) => {
    setReferralLink(newLink);
  };

  const [errorMessage, setErrorMessage] = useState<{
    message: string;
    details?: any;
  } | null>(null);

  const [buyForUsdt, setBuyForUsdt] = useState(false);

  const {
    chainId,
    handleNetworkChange,
    supportedNetworks: availableNetworks,
  } = useNetworkManager({
    supportedNetworks: idoData?.supportedNetworks || [],
    autoSwitch: true,
  });

  const [currentNetworkData, setCurrentNetworkData] = useState<any | null>(
    null
  );

  useEffect(() => {
    if (!chainId || !availableNetworks.length) return;
    const networkData = availableNetworks.find(
      (network: any) => network.chainId === chainId
    );
    setCurrentNetworkData(networkData || null);

    if (!networkData && availableNetworks.length > 0) {
      console.log(`Network ${chainId} not found, using fallback`);
    }
  }, [chainId, availableNetworks]);

  const contractAddress = currentNetworkData?.sellingAddress;
  const usdtAddress = currentNetworkData?.usdtAddress;
  const usdcAddress = currentNetworkData?.usdcAddress;

  const [purchaseToken, setPurchaseToken] = useState<string>(usdtAddress);

  useEffect(() => {
    if (usdtAddress || usdcAddress) {
      setPurchaseToken(buyForUsdt ? usdtAddress : usdcAddress);
    }
  }, [usdtAddress, usdcAddress, buyForUsdt]);

  // Add validation for network configuration
  const isValidNetwork = contractAddress && (usdtAddress || usdcAddress);
  if (!isValidNetwork && currentNetworkData) {
    console.error(`Invalid network configuration for chainId ${chainId}:`, {
      contractAddress,
      usdtAddress,
      usdcAddress,
      currentNetworkData,
    });
  }

  const { signMessageAsync } = useSafeSignMessage();
  const { switchChainAsync } = useSwitchChain();
  const { address, chainId: walletChainId, connector } = useAccount();
  const currentDate = new Date();

  const {
    data: hash,
    error,
    isPending,
    writeContractAsync,
  } = useWriteContract();

  const { data: lastPurchasesData } = useGetLatestPurchasesQuery(idoId, {
    pollingInterval: ONE_MINUTE,
  });

  useEffect(() => {
    if (showBuyNotification) {
      setModalOpen(true);
    }
  }, [showBuyNotification]);

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const {
    isLoading: isTxLoading,
    isSuccess: isTxSuccess,
    isError: isApproveError,
    error: approveError,
  } = useWaitForTransactionReceipt({
    hash: approveHash,
  });

  const supportedChainIds = getSupportedChainIds(availableNetworks);
  const selectedChainId = normalizeChainId(chainId);
  const selectedChainIsSupported = isSupportedChainId(
    selectedChainId,
    supportedChainIds
  );

  const getSaleFlowLogData = (
    phase: string,
    extra: Record<string, any> = {}
  ) => ({
    phase,
    walletAddress: address,
    connectorId: connector?.id,
    walletName: connector?.name,
    chainId: selectedChainId,
    walletChainId,
    expectedChainIds: supportedChainIds,
    contractAddress,
    purchaseToken,
    tokensToReceive,
    ...extra,
  });

  const finishPurchaseFlow = () => {
    purchaseFlowRef.current = null;
    purchaseSignatureRef.current = null;
    setPurchaseFlowStage(null);
  };

  const clearPurchaseReceiptState = () => {
    setBuyingHash(null);
    setBuyTxChainId(null);
    setWalletActionPending(null);
    setShowWalletActionNotification(false);
    setIsLoading(false);
  };

  const startPurchaseFlow = () => {
    if (purchaseFlowRef.current) {
      handleError(SALE_PENDING_ERROR, { stage: purchaseFlowRef.current });
      logPurchaseError(
        idoId || "",
        amountInUSD,
        activeTab.label,
        new Error(SALE_PENDING_ERROR),
        getSaleFlowLogData(purchaseFlowRef.current)
      );
      return false;
    }

    purchaseFlowRef.current = SALE_FLOW_STAGE.signature;
    setPurchaseFlowStage(SALE_FLOW_STAGE.signature);
    return true;
  };

  const ensureSupportedSaleChain = async () => {
    if (!selectedChainIsSupported || !selectedChainId) {
      throw new Error(SALE_SUPPORTED_CHAIN_ERROR);
    }

    if (!currentNetworkData || !contractAddress || !purchaseToken) {
      throw new Error("Sale network configuration is incomplete.");
    }

    if (walletChainId !== selectedChainId) {
      setPurchaseFlowStage(SALE_FLOW_STAGE.networkSwitch);
      setWalletActionPending("Switching to the correct network...");
      await switchChainAsync({ chainId: selectedChainId });
    }

    return selectedChainId;
  };

  useEffect(() => {
    if (purchaseDetails) {
      setCurrentPurchase(purchaseDetails);
    }
  }, [purchaseDetails]);

  const handleError = (message: string, error: any) => {
    setErrorMessage({ message, details: error });
    setIsLoading(false);

    // Log the error for tracking
    logJavaScriptError(new Error(message), "SaleWidget_handleError");
  };

  // Backstop: release a stuck sale-flow lock if a stage never settles
  // (hung wallet popup or WalletConnect relay / RPC), so the user is not
  // blocked from retrying without a page reload. Re-armed on every stage.
  useEffect(() => {
    if (!purchaseFlowStage) return;

    const timeoutId = setTimeout(() => {
      logPurchaseError(
        idoId || "",
        amountInUSD,
        activeTab.label,
        new Error(SALE_FLOW_TIMEOUT_ERROR),
        getSaleFlowLogData(purchaseFlowStage)
      );
      handleError(SALE_FLOW_TIMEOUT_ERROR, { stage: purchaseFlowStage });
      clearPurchaseReceiptState();
      finishPurchaseFlow();
    }, SALE_FLOW_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseFlowStage]);

  const handleSolanaError = (error: string) => {
    setSolanaError(error || null);
  };

  const {
    isLoading: isBuyLoading,
    isSuccess: isBuySuccess,
    isError: isBuyError,
    error: buyError,
    data: buyReceipt,
  } = useWaitForTransactionReceipt({
    hash: buyingHash,
    chainId: buyTxChainId || undefined,
  });

  useEffect(() => {
    if (isBuySuccess !== true || !buyingHash) return;

    // wagmi resolves the receipt even when the tx REVERTED on-chain, so a
    // reverted purchase reaches this branch. Treat it as a failure (most often
    // an insufficient-balance revert) instead of reporting a fake success.
    if (buyReceipt?.status === "reverted") {
      console.error("[IDO][buy] tx reverted", {
        hash: buyingHash,
        chainId: buyTxChainId,
      });
      logPurchaseError(
        idoId || "",
        amountInUSD,
        activeTab.label,
        new Error("Purchase transaction reverted on-chain"),
        getSaleFlowLogData(SALE_FLOW_STAGE.purchaseReceipt, {
          purchaseHash: buyingHash,
          receiptStatus: "reverted",
        })
      );
      handleError(buildSaleRevertedError(buyingHash, buyTxChainId), {
        hash: buyingHash,
      });
      clearPurchaseReceiptState();
      finishPurchaseFlow();
      return;
    }

    trackSaleSuccess({
      idoId,
      transactionId: String(buyingHash),
      chainId: buyTxChainId ?? chainId ?? null,
      paymentToken: activeTab.label,
      contributionAmount: amountInUSD,
      valueUsd: amountInUSD,
      tokensToReceive,
    });
    clearPurchaseReceiptState();
    finishPurchaseFlow();

    const newTickerAmount = parseFloat(tokensToReceive);
    if (!isNaN(newTickerAmount)) {
      setIsPurchaseSuccessful(true);
      setIsCancelSuccessful(false);
    }

    // Log successful purchase after receipt confirmation
    logPurchaseSuccess(
      idoId || "",
      amountInUSD,
      activeTab.label,
      buyingHash || "",
      {
        walletAddress: address,
        tokensToReceive,
        contractAddress,
        chainId,
        purchaseToken,
        transactionHash: buyingHash,
      }
    );

    onPurchaseSuccess();
    setShowBuyNotification(true);
  }, [isBuySuccess, buyReceipt]);

  useEffect(() => {
    if (!isBuyError) return;
    console.error("[IDO][buy] receipt error", {
      hash: buyingHash,
      chainId: buyTxChainId,
      error: buyError,
    });
    logPurchaseError(
      idoId || "",
      amountInUSD,
      activeTab.label,
      buyError instanceof Error
        ? buyError
        : new Error("Purchase receipt failed"),
      getSaleFlowLogData(SALE_FLOW_STAGE.purchaseReceipt, {
        purchaseHash: buyingHash,
      })
    );
    handleError(buildSaleReceiptError(buyingHash, buyTxChainId), buyError);
    clearPurchaseReceiptState();
    finishPurchaseFlow();
  }, [isBuyError, buyError, buyingHash, buyTxChainId]);

  useEffect(() => {
    (async () => {
      if (isTxSuccess === true && !!approveHash) {
        setApproveHash(null);
        setWalletActionPending(
          "Approval successful! Proceeding with token purchase..."
        );
        await buyTokens(purchaseSignatureRef.current);
      }
    })();
    if (isTxSuccess === true) {
      setShowTxNotification(true);
    }
  }, [isTxSuccess]);

  useEffect(() => {
    if (!isApproveError) return;
    console.error("[IDO][approve] receipt error", {
      hash: approveHash,
      chainId: selectedChainId,
      error: approveError,
    });
    logPurchaseError(
      idoId || "",
      amountInUSD,
      activeTab.label,
      approveError instanceof Error
        ? approveError
        : new Error("Approval receipt failed"),
      getSaleFlowLogData(SALE_FLOW_STAGE.approvalReceipt, {
        approvalHash: approveHash,
      })
    );
    handleError("Token approval confirmation failed.", approveError);
    setApproveHash(undefined);
    setWalletActionPending(null);
    setShowWalletActionNotification(false);
    setIsLoading(false);
    finishPurchaseFlow();
  }, [isApproveError, approveError, approveHash]);

  const { data: usdtAllowance, refetch: refetchUsdtAllowance } =
    useReadContract({
      address: usdtAddress as `0x${string}`,
      abi: tokenABI,
      functionName: "allowance",
      args: [address, contractAddress],
      query: {
        enabled: Boolean(
          usdtAddress && address && contractAddress && isValidNetwork
        ),
      },
    });

  const { data: usdcAllowance, refetch: refetchUsdcAllowance } =
    useReadContract({
      address: usdcAddress as `0x${string}`,
      abi: tokenABI,
      functionName: "allowance",
      args: [address, contractAddress],
      query: {
        enabled: Boolean(
          usdcAddress && address && contractAddress && isValidNetwork
        ),
      },
    });

  const { data: price } = useReadContract({
    address: contractAddress as `0x${string}`,
    abi: ABI,
    functionName: "price",
    query: {
      enabled: Boolean(contractAddress && isValidNetwork),
    },
  });

  const { data: contractMinPurchaseAmount } = useReadContract({
    address: contractAddress as `0x${string}`,
    abi: ABI,
    functionName: "minPurchaseAmount",
    query: {
      enabled: Boolean(contractAddress && isValidNetwork),
    },
  });

  const effectiveMinAllocation = getEffectiveMinAllocationUsd({
    dbMinAllocationUsd: currentAllocation.minAllocation,
    contractMinPurchaseAmount:
      typeof contractMinPurchaseAmount === "bigint"
        ? contractMinPurchaseAmount
        : null,
    tokenPriceUsd: currentAllocation.tokenPrice,
  });

  const { data: bought } = useReadContract({
    address: contractAddress as `0x${string}`,
    abi: ABI,
    functionName: "balances",
    args: [address],
    query: {
      enabled: Boolean(contractAddress && address && isValidNetwork),
    },
  });

  useEffect(() => {
    if (!address || !idoId || !isAuth) return;

    const fetchData = async () => {
      try {
        const token = tokenService.tokens?.accessToken;
        const response = await axios.get(
          `${process.env.REACT_APP_API_SECONDARY}/application/legalSignature/check/${idoId}/${address}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data) {
          setIsSignatureSaved(true);
          setMySignature("0xSignature");
        } else {
          setIsSignatureSaved(false);
        }
      } catch (error) {
        console.error("Ошибка проверки подписи:", error);
      }
    };

    fetchData();
  }, [address, idoId]);

  const saveLegalSignature = async (sign: string) => {
    try {
      if (!address || !idoId || !sign || !isAuth) return;

      const body = {
        address,
        signature: sign,
        message,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_SECONDARY}/application/legalSignature/${idoId}`,
        body,
        {
          headers: {
            Authorization: `Bearer ${tokenService.tokens?.accessToken}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Ошибка сохранения подписи:", error);
      handleError("Failed to save signature. Please, try again.", null);
    }
  };

  const signMessage = async () => {
    if (!startPurchaseFlow()) return;

    try {
      if (!address) {
        finishPurchaseFlow();
        return;
      }
      await ensureSupportedSaleChain();

      setErrorMessage(null);

      // Log purchase attempt
      logPurchaseAttempt(idoId || "", amountInUSD, activeTab.label, {
        ...getSaleFlowLogData(SALE_FLOW_STAGE.signature),
      });

      // Show mobile wallet action notification with device-specific messaging
      const pendingMessage = isMobile
        ? "Please check your wallet app to sign the terms and conditions."
        : "Please check your wallet extension to sign the terms and conditions.";
      setWalletActionPending(pendingMessage);
      setShowWalletActionNotification(true);

      if (isSignatureSaved) {
        return approveToken(mySignature);
      }

      const signature = await signMessageAsync({
        address,
        connector,
        message,
      });

      setMySignature(signature);

      setWalletActionPending("Saving your signature...");
      const res = await saveLegalSignature(signature);

      if (!res) {
        setWalletActionPending(null);
        setShowWalletActionNotification(false);
        handleError("Failed to save signature. Please, try again.", null);
        finishPurchaseFlow();
        return;
      }

      setIsSignatureSaved(true);
      setWalletActionPending("Preparing token approval...");
      approveToken(signature);
    } catch (e) {
      console.error("Ошибка в процессе подписания:", e);
      setWalletActionPending(null);
      setShowWalletActionNotification(false);

      // Log purchase error during signature phase
      logPurchaseError(
        idoId || "",
        amountInUSD,
        activeTab.label,
        e instanceof Error ? e : new Error("Failed to sign message"),
        {
          ...getSaleFlowLogData(SALE_FLOW_STAGE.signature),
        }
      );

      handleError("Failed to sign the message.", e);
      finishPurchaseFlow();
    }
  };

  // Only call useBalance if we have a valid chainId and token addresses
  const shouldFetchBalance = Boolean(
    address &&
      chainId &&
      (buyForUsdt ? usdtAddress : usdcAddress) &&
      isValidNetwork
  );

  const result = useBalance({
    address: address,
    token: buyForUsdt ? usdtAddress : usdcAddress,
    chainId: chainId,
    query: {
      enabled: shouldFetchBalance,
    },
  });
  useEffect(() => {
    if (!shouldFetchBalance) return;
    if (result.error) {
      console.error("[IDO][balance] error", {
        chainId,
        address,
        token: buyForUsdt ? "USDT" : "USDC",
        tokenAddress: buyForUsdt ? usdtAddress : usdcAddress,
        error: result.error,
      });
    } else if (result.data) {
      console.log("[IDO][balance] ok", {
        chainId,
        address,
        token: buyForUsdt ? "USDT" : "USDC",
        tokenAddress: buyForUsdt ? usdtAddress : usdcAddress,
        formatted: result.data.formatted,
        value: result.data.value?.toString?.(),
      });
    }
  }, [
    shouldFetchBalance,
    result.error,
    result.data,
    chainId,
    address,
    buyForUsdt,
    usdtAddress,
    usdcAddress,
  ]);

  //HASK _sign as params because of the error
  const approveToken = async (_sign: string | null = mySignature) => {
    try {
      if (!address || !_sign) {
        finishPurchaseFlow();
        return;
      }
      // Single-flight guard: ignore re-entry once approval is already running.
      if (
        purchaseFlowRef.current === SALE_FLOW_STAGE.approval ||
        purchaseFlowRef.current === SALE_FLOW_STAGE.approvalReceipt
      ) {
        return;
      }
      purchaseSignatureRef.current = _sign;

      setIsLoading(true);
      purchaseFlowRef.current = SALE_FLOW_STAGE.approval;
      setPurchaseFlowStage(SALE_FLOW_STAGE.approval);
      const safeChainId = await ensureSupportedSaleChain();

      const stableCoin = buyForUsdt ? usdtAddress : usdcAddress;
      const decimals = getStablecoinDecimals(safeChainId);
      console.log("[IDO][approve] start", {
        chainId,
        address,
        stableCoin,
        decimals,
        amountInUSD,
        allowance: buyForUsdt ? usdtAllowance : usdcAllowance,
      });

      // Re-read the on-chain balance fresh so a stale/missing cached value can't
      // let an underfunded purchase through (it would only revert and waste gas).
      // IMPORTANT: respect the result and abort the flow when funds are short.
      const freshBalance = await result.refetch();
      const hasEnoughBalance = checkBalance({
        formatted: freshBalance?.data?.formatted ?? result.data?.formatted,
        amountInUSD,
        activeTabLabel: buyForUsdt ? Currency.USDT : Currency.USDC,
        handleError,
        setIsLoading,
      });
      if (!hasEnoughBalance) {
        finishPurchaseFlow();
        return;
      }

      // Re-read allowance fresh (public RPC) so a stale cached value cannot
      // skip a required approve or send a buy that reverts on-chain.
      const refetchAllowance = buyForUsdt
        ? refetchUsdtAllowance
        : refetchUsdcAllowance;
      const freshAllowance = await refetchAllowance();
      const allowanceBN =
        freshAllowance?.data ?? (buyForUsdt ? usdtAllowance : usdcAllowance);
      const allowance = Number(allowanceBN) / 10 ** decimals;
      const approveAmount = parseUnits(amountInUSD.toString(), decimals);
      console.log("[IDO][approve] allowance", {
        allowanceBN: allowanceBN?.toString?.(),
        allowance,
        approveAmount: approveAmount?.toString?.(),
      });

      if (allowance && allowance >= Number(amountInUSD)) {
        console.log("[IDO][approve] allowance sufficient, proceed to buy");
        setWalletActionPending("Proceeding to token purchase...");
        return buyTokens(_sign);
      }

      const approveMessage = isMobile
        ? `Please approve ${
            buyForUsdt ? "USDT" : "USDC"
          } spending in your wallet app.`
        : `Please approve ${
            buyForUsdt ? "USDT" : "USDC"
          } spending in your wallet extension.`;
      setWalletActionPending(approveMessage);
      const txHash = await writeContractAsync({
        address: stableCoin as `0x${string}`,
        abi: tokenABI,
        chainId: safeChainId,
        functionName: "approve",
        args: [contractAddress, approveAmount],
      });
      console.log("[IDO][approve] tx sent", { txHash });

      setWalletActionPending(
        `Waiting for ${buyForUsdt ? "USDT" : "USDC"} approval confirmation...`
      );
      setApproveHash(txHash);
    } catch (error: any) {
      console.error("Approval error: ", error);
      setWalletActionPending(null);
      setShowWalletActionNotification(false);

      // Log purchase error during approval phase
      logPurchaseError(
        idoId || "",
        amountInUSD,
        activeTab.label,
        error instanceof Error ? error : new Error("Token approval failed"),
        {
          ...getSaleFlowLogData(SALE_FLOW_STAGE.approval),
          errorMessage: error.cause?.shortMessage || "Unknown error",
        }
      );

      handleError(
        "Token approval failed.",
        error.cause?.shortMessage || "Unknown error"
      );
      setIsLoading(false);
      finishPurchaseFlow();
    }
  };

  const buyTokens = async (_sign: string | null = mySignature) => {
    try {
      if (!address || !_sign) {
        console.error("Address or signature missing");
        finishPurchaseFlow();
        return;
      }
      // Single-flight guard: the approval-receipt effect and the direct call
      // can both reach buy; only the first one is allowed through.
      if (
        purchaseFlowRef.current === SALE_FLOW_STAGE.purchase ||
        purchaseFlowRef.current === SALE_FLOW_STAGE.purchaseReceipt
      ) {
        return;
      }

      setIsLoading(true);
      purchaseFlowRef.current = SALE_FLOW_STAGE.purchase;
      setPurchaseFlowStage(SALE_FLOW_STAGE.purchase);
      const safeChainId = await ensureSupportedSaleChain();

      const tokenAmount = parseUnits(tokensToReceive, 18);

      if (!tokenAmount || tokenAmount <= 0) {
        throw new Error("Invalid token amount");
      }
      console.log("[IDO][buy] start", {
        chainId: safeChainId,
        address,
        contractAddress,
        amountInUSD,
        tokensToReceive,
        tokenAmount: tokenAmount?.toString?.(),
      });

      setWalletActionPending("Preparing purchase transaction...");

      const requestBody = {
        chainId: safeChainId,
        address: address,
        idoId: idoId,
      };

      const token = tokenService.tokens?.accessToken;
      if (!token) {
        throw new Error("Authorization token is missing");
      }

      let response;
      try {
        response = await axios.post(
          `${process.env.REACT_APP_API_SECONDARY}/ido/signature`,
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("[IDO][buy] signature ok", {
          status: response?.status,
          data: response?.data,
        });
      } catch (error: any) {
        console.error(
          "Error during signature request:",
          error.response?.data || error.message
        );
        console.error("[IDO][buy] signature error", {
          status: error?.response?.status,
          data: error?.response?.data,
          message: error?.message,
        });
        throw new Error("Failed to fetch signature from backend");
      }

      if (!response.data || !response.data.signature || !response.data.data) {
        throw new Error("Invalid response data from backend");
      }

      const {
        deadline,
        maxClaim,
        minClaim,
        refFee,
        refAddress,
        totalBought,
        _refundFee,
      } = response.data.data;

      const value = 0;

      const purchaseMessage = isMobile
        ? "Please confirm the token purchase in your wallet app."
        : "Please confirm the token purchase in your wallet extension.";
      setWalletActionPending(purchaseMessage);

      const bHash = await writeContractAsync({
        address: contractAddress as `0x${string}`,
        abi: ABI,
        chainId: safeChainId,
        functionName: "buyTokens",
        args: [
          tokenAmount.toString(),
          purchaseToken,
          deadline,
          minClaim,
          maxClaim,
          totalBought,
          refAddress,
          refFee,
          _refundFee,
          response.data.signature,
        ],
        value: parseUnits(value.toString(), 18),
      });
      console.log("[IDO][buy] tx sent", { txHash: bHash });

      if (!bHash) {
        throw new Error("Transaction was not sent or hash missing");
      }

      setWalletActionPending("Waiting for purchase confirmation...");
      setPurchaseFlowStage(SALE_FLOW_STAGE.purchaseReceipt);
      setBuyingHash(bHash);
      setBuyTxChainId(safeChainId);
    } catch (error: any) {
      setWalletActionPending(null);
      setShowWalletActionNotification(false);

      // Log purchase error during buying phase
      logPurchaseError(
        idoId || "",
        amountInUSD,
        activeTab.label,
        error instanceof Error ? error : new Error("Token purchase failed"),
        {
          ...getSaleFlowLogData(SALE_FLOW_STAGE.purchase),
          purchaseToken,
          isAxiosError: axios.isAxiosError(error),
          errorData: axios.isAxiosError(error)
            ? error.response?.data
            : error.message,
        }
      );

      if (axios.isAxiosError(error)) {
        console.error("Axios error:", error.response?.data || error.message);
      } else {
        console.error("Error buying tokens:", error.message || error);
        handleError(
          "An unexpected error occurred during the token purchase.",
          error.message
        );
      }
      finishPurchaseFlow();
    } finally {
      setIsLoading(false);
    }
  };

  const isLoadingPurchase =
    isLoading || isLoadingAA || isTxLoading || isBuyLoading;

  const handleInputChange = (value: string) => {
    const amount = parseFloat(value);
    setAmountInUSD(value);
    if (!isNaN(amount)) {
      setTokensToReceive((amount / currentAllocation.tokenPrice).toFixed(2));
    } else {
      setTokensToReceive("");
    }
  };

  const isOutsideAllocation = () => {
    const amount = parseFloat(amountInUSD);
    return (
      !isNaN(amount) &&
      (amount < effectiveMinAllocation ||
        amount > currentAllocation.maxAllocation)
    );
  };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    if (tab.label === Currency.USDT) {
      setPurchaseToken(usdtAddress);
      setBuyForUsdt(true);
    } else if (tab.label === Currency.USDC) {
      setPurchaseToken(usdcAddress);
      setBuyForUsdt(false);
    } else if (tab.label === Currency.ETH) {
      setPurchaseToken(zeroAddress);
      setBuyForUsdt(false);
    }
  };

  return (
    <div
      className={`${styles.containerWrapper} ${
        theme === "dark" ? styles.darkTheme : ""
      }`}
    >
      {currentAllocation.usedAllocation <= 0 ? (
        <h4 className={styles.title}>Get {tokenTicker} Tokens</h4>
      ) : (
        <h4 className={styles.title}>Get more {tokenTicker} Tokens</h4>
      )}
      <RunningLine
        ticker={idoData.tokenTicker || "$TICKER"}
        purchases={lastPurchasesData || []}
      />
      <div className={styles.wrapperContainer}>
        <div className={styles.left}>
          <div
            className={`${styles.widget} ${
              theme === "dark" ? styles.darkTheme : ""
            }`}
          >
            {currentAllocation.usedAllocation > 0 && currentPurchase && (
              <div className={styles.purchaseInfo}>
                <h3 className={styles.title}>Purchase Successful!</h3>
                <p className={styles.text}>
                  You've purchased{" "}
                  <b>
                    {currentPurchase.totalTokensPurchased.toLocaleString()}{" "}
                    {tokenTicker}
                  </b>{" "}
                  for{" "}
                  {isLoadingPurchase ? (
                    <CircleLoader isLoading={isLoadingPurchase} />
                  ) : (
                    <b>${currentAllocation.usedAllocation.toLocaleString()}</b>
                  )}
                </p>
                <div
                  className={`${styles.balanceBox} ${
                    theme === "dark" ? styles.darkTheme : ""
                  }`}
                >
                  <h4>Your Balance:</h4>
                  <p
                    className={`${styles.balanceBoxText} ${
                      theme === "dark" ? styles.darkTheme : ""
                    }`}
                  >
                    <div className={styles.purchBlock}>
                      <b>Purchased:</b>
                      <span>
                        {currentPurchase.totalTokensPurchased.toLocaleString()}{" "}
                        {tokenTicker}
                      </span>
                    </div>
                  </p>
                  <p
                    className={`${styles.balanceBoxText} ${
                      theme === "dark" ? styles.darkTheme : ""
                    }`}
                  >
                    <div className={styles.purchBlock}>
                      <b>Vesting:</b>
                      <span>{currentPurchase.vestingDetails}</span>
                    </div>
                  </p>
                </div>
                <div className={styles.disclaimer}>
                  <p
                    className={`${styles.text} ${
                      theme === "dark" ? styles.darkTheme : ""
                    }`}
                  >
                    Disclaimer: Your purchase is subject to the terms &
                    conditions of this IDO. {tokenTicker} tokens are subject to
                    TGE and vesting schedules. Ensure compliance with your local
                    regulations.
                  </p>
                </div>
                <p className={styles.textDisc}>
                  Want to buy more? Check your remaining allocation below.
                </p>
              </div>
            )}
            {saleDetails.isSaleOpen && (
              <div className={styles.purchaseForm}>
                <div className={styles.inputs}>
                  {chainId !== 80094 && (
                    <label className={styles.label}>
                      <span>Select Token:</span>
                      <div className={styles.tabs}>
                        {tabs.map((tab) => (
                          <button
                            key={tab.label}
                            className={`${styles.tab} ${
                              activeTab.label === tab.label ? styles.active : ""
                            }`}
                            onClick={() => handleTabChange(tab)}
                          >
                            <img
                              src={tab.icon}
                              alt={tab.label}
                              className={styles.icon}
                            />
                            <p>{tab.label}</p>
                          </button>
                        ))}
                      </div>
                    </label>
                  )}
                  <NetworkSelector
                    type="preorder"
                    supportedNetworks={availableNetworks}
                    currentChainId={chainId}
                    onNetworkChange={handleNetworkChange}
                  />
                  <label className={styles.label}>
                    <span>
                      Enter Amount in{" "}
                      {buyForUsdt ? Currency.USDT : Currency.USDC}:
                    </span>
                    <div className={styles.inputWrapper}>
                      <input
                        type="number"
                        className={`${styles.inputToken} ${
                          theme === "dark" ? styles.darkTheme : ""
                        }`}
                        value={amountInUSD}
                        onChange={(e) => handleInputChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e") {
                            e.preventDefault();
                          }
                        }}
                        placeholder={`${
                          currentAllocation.availableAllocation < 1
                            ? 0
                            : currentAllocation.availableAllocation
                        } ${buyForUsdt ? Currency.USDT : Currency.USDC}`}
                        max={
                          currentAllocation.availableAllocation < 1
                            ? 0
                            : currentAllocation.availableAllocation
                        }
                      />
                      <img
                        src={activeTab.icon}
                        alt={activeTab.label}
                        className={styles.currencyIcon}
                      />
                    </div>
                  </label>
                  {isOutsideAllocation() && isAuth && (
                    <p className={styles.warningMessage}>
                      You can purchase tokens in the range of{" "}
                      {effectiveMinAllocation} to{" "}
                      {currentAllocation.availableAllocation}{" "}
                      {buyForUsdt ? Currency.USDT : Currency.USDC}.
                    </p>
                  )}
                  <label>
                    <span>Amount {tokenTicker} You Receive:</span>
                    <div className={styles.inputWrapper}>
                      <input
                        className={`${styles.inputToken} ${
                          theme === "dark" ? styles.darkTheme : ""
                        }`}
                        type="text"
                        value={
                          tokensToReceive && !isNaN(Number(tokensToReceive))
                            ? Number(tokensToReceive).toFixed(2)
                            : "0.00"
                        }
                        readOnly
                        placeholder={`0 ${tokenTicker}`}
                      />
                      <img
                        src={
                          idoData.media?.find((m: any) => m.label === "logo")
                            ?.value || ""
                        }
                        alt="Ticker"
                        className={styles.currencyIcon}
                      />
                    </div>
                  </label>
                </div>
                {isAuth && (
                  <p className={styles.allocationInfo}>
                    Your allocation range is Min.{" "}
                    {effectiveMinAllocation.toLocaleString()}{" "}
                    {buyForUsdt ? Currency.USDT : Currency.USDC} - Max.{" "}
                    {currentAllocation.availableAllocation.toLocaleString()}{" "}
                    {buyForUsdt ? Currency.USDT : Currency.USDC}
                  </p>
                )}

                {/* Solana Wallet Section */}
                {requiresSolana && (
                  <div
                    className={`${styles.solanaWalletSection} ${
                      theme === "dark" ? styles.darkTheme : ""
                    }`}
                  >
                    {solanaAssociationError ? (
                      <div className={styles.solanaErrorAlert}>
                        <div className={styles.solanaAlertIcon}>❌</div>
                        <div className={styles.solanaAlertContent}>
                          <h4 className={styles.solanaWalletTitle}>
                            Solana Association Check Failed
                          </h4>
                          <p className={styles.solanaWalletDescription}>
                            {solanaAssociationError}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.solanaAlert}>
                        <div className={styles.solanaAlertIcon}>⚠️</div>
                        <div className={styles.solanaAlertContent}>
                          <h4 className={styles.solanaWalletTitle}>
                            Solana Wallet Required
                          </h4>
                          <p className={styles.solanaWalletDescription}>
                            This IDO requires both EVM and Solana wallet
                            connections.
                          </p>
                        </div>
                      </div>
                    )}
                    <SolanaConnectButton
                      className={styles.solanaConnectButton}
                      disabled={!isAuth}
                      onError={handleSolanaError}
                    />
                  </div>
                )}

                {requiresTon && (
                  <TonWalletGate
                    disabled={!isAuth}
                    associationState={tonAssociationState}
                  />
                )}

                {isLoadingPurchase ? (
                  <Button
                    className={styles.buyButton}
                    disabled={isLoadingPurchase}
                  >
                    <CircleLoader
                      colorType={theme === "dark" ? "light" : "dark"}
                      className={styles.loader}
                      isLoading={isLoadingPurchase}
                    />
                    Loading
                  </Button>
                ) : (
                  <Button
                    className={styles.buyButton}
                    variant="pink"
                    disabled={
                      !amountInUSD ||
                      parseFloat(amountInUSD) <
                        effectiveMinAllocation ||
                      parseFloat(amountInUSD) >
                        currentAllocation.availableAllocation ||
                      currentDate < sellStartDate ||
                      totalTokens <= totalBalance ||
                      currentDate > sellEndDate ||
                      !isValidNetwork ||
                      !selectedChainIsSupported ||
                      !!purchaseFlowStage ||
                      (requiresSolana &&
                        (!isSolanaAssociated ||
                          !!solanaError ||
                          !!solanaAssociationError)) ||
                      (requiresTon &&
                        (!isTonAssociated || !!tonAssociationError))
                    }
                    onClick={() => {
                      logButtonClick("buy-tokens-button", "SaleWidget", {
                        idoId,
                        amount: amountInUSD,
                        currency: activeTab.label,
                        walletAddress: address,
                        chainId: selectedChainId,
                        walletChainId,
                        expectedChainIds: supportedChainIds,
                        tokenTicker,
                      });
                      signMessage();
                    }}
                  >
                    Get {tokenTicker}
                  </Button>
                )}
                {errorMessage && (
                  <div className={styles.error}>
                    <p>
                      Error: {errorMessage.message} Please, refresh the page and
                      try again
                    </p>
                  </div>
                )}
                {solanaError && (
                  <div className={styles.error}>
                    <p>Solana Wallet Error: {solanaError}</p>
                  </div>
                )}
                {walletActionPending ? (
                  <div
                    className={`${styles.walletActionStatus} ${
                      theme === "dark" ? styles.darkTheme : ""
                    }`}
                  >
                    <CircleLoader
                      colorType={theme === "dark" ? "light" : "dark"}
                      isLoading={true}
                    />
                    <p
                      className={`${styles.walletActionMessage} ${
                        theme === "dark" ? styles.darkTheme : ""
                      }`}
                    >
                      {walletActionPending}
                    </p>
                  </div>
                ) : (
                  <p className={styles.walletMessage}>
                    <i>Sign Terms in your Wallet to Continue</i>
                  </p>
                )}
              </div>
            )}
            <div className={styles.saleInfo}>
              <p>
                After your purchase, tokens will appear in your balance. You can
                track your allocation on the dashboard.
              </p>
            </div>
            <Notification
              visible={showBuyNotification}
              message="Purchase completed successfully."
              type="success"
              onClose={() => setShowBuyNotification(false)}
            />
            <Notification
              visible={showWalletActionNotification && !!walletActionPending}
              message={walletActionPending || ""}
              type="info"
              onClose={() => {
                setShowWalletActionNotification(false);
                setWalletActionPending(null);
              }}
              duration={5000}
            />
            <RefModal
              onUpdateReferralLink={handleReferralLinkUpdate}
              canGenerateText={idoData.canGenerateText}
              isOpen={modalOpen}
              onClose={handleCloseModal}
              isPreSale={false}
            />
          </div>
        </div>
        <div className={styles.right}>
          <div className={styles.inviteWrapper}>
            <InviteSection
              ticker={tokenTicker}
              isPreWL={false}
              isAuth={isAuth}
              refLink={referralLink}
              theme={theme}
              currentPurchase={currentPurchase?.totalTokensPurchased}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaleWidget;
